import React, { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import Button from "../../components/Core/Button/Button";
import TextArea from "../../components/Core/TextArea/TextArea";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import Divider from "../../components/Shared/Divider/Divider";
import InfoBlock from "../../components/Shared/InfoBlock/InfoBlock";
import { ReportsBreadcrumbsDetails } from "../../configs/breadcrumbNavigation";
import { useJobReport, useUpdateJobReport } from "../../networking/job-report.hooks";
import { StarIcon } from "@chakra-ui/icons";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { consultantRoute, customerRoute, jobRoute } from "../../utils/route.utils";

interface JobReportParamProps {
	jobReportId: string;
}

const JobReportDetails: React.FC = () => {
	const { t } = useTranslation();

	const { jobReportId } = useParams<keyof JobReportParamProps>() as JobReportParamProps;
	const { data: jobReport, isLoading } = useJobReport(jobReportId);
	const { mutate: updateJobReport, isLoading: isUpdating } = useUpdateJobReport();

	const [form, setForm] = useState(jobReport?.form || []);

	useEffect(() => {
		setForm(jobReport?.form || []);
	}, [jobReport]);

	const onSubmit = async (e: FormEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (jobReportId) {
			const formUpdate = form.reduce((acc, curr) => {
				acc[curr.id] = curr.value;
				return acc;
			}, {} as any);

			updateJobReport({ id: jobReportId, form: formUpdate });
		}
	};

	function updateForm(rowIndex: number, val: string) {
		form[rowIndex].value = val;
		setForm([...form]);
	}

	if (isLoading) {
		return <LoadingIndicator size={"m"} />;
	}

	return (
		<div className="p-4 px-6">
			<React.Fragment>
				<Breadcrumbs items={ReportsBreadcrumbsDetails} />
				<Flex>
					<Flex width="25%" direction="column">
						<InfoBlock
							label="CUSTOMER"
							value={`${jobReport?.job.customer?.name}`}
							link={customerRoute(jobReport?.job.customer.id || "")}
						/>
						<InfoBlock
							label="JOB_ID"
							value={`#${jobReport?.job.orderNumber}`}
							link={jobRoute(jobReport?.job.id || "")}
						/>
					</Flex>
					<Flex direction="column">
						<InfoBlock
							label="RATING"
							value={
								jobReport?.rating === undefined
									? t("jobReport.ratingNotSet")
									: `${jobReport.rating} / 5`
							}
						/>
						<InfoBlock
							label="CONSULTANT"
							value={`${jobReport?.consultant.firstName} ${jobReport?.consultant.lastName}`}
							link={consultantRoute(jobReport?.consultant.id || "")}
						/>
					</Flex>
				</Flex>
				<Divider />
				<form>
					<Flex direction="column" margin="1.5rem 0" gap="1.25rem">
						{form.map((row, i) => (
							<TextArea
								key={i}
								width="50%"
								height="6.25rem"
								label={`${row.label} ${row.publishToCustomer ? "(publiceras till kund)" : ""}`}
								onChangeText={(val) => updateForm(i, val)}
								value={form[i].value}
								containerMargin="0"
							/>
						))}

						<Button width="10rem" onClick={onSubmit} loading={isUpdating}>
							{t("SAVE_CHANGES")}
						</Button>
					</Flex>
				</form>
			</React.Fragment>
		</div>
	);
};

export default JobReportDetails;
